import axios, { AxiosError } from 'axios'
import { defaultRequestInterceptors, defaultResponseInterceptors } from './config'

import { AxiosInstance, InternalAxiosRequestConfig, RequestConfig, AxiosResponse } from './types'
import { ElMessage } from 'element-plus'
import { REQUEST_TIMEOUT } from '@/constants'
import { HTTP_STATUS_MESSAGE } from '@/constants/errorCode'
import { useUserStoreWithOut } from '@/store/modules/user'

export const PATH_URL = import.meta.env.VITE_API_BASE_PATH

const abortControllerMap: Map<string, AbortController> = new Map()

const axiosInstance: AxiosInstance = axios.create({
  timeout: REQUEST_TIMEOUT,
  baseURL: PATH_URL
})

axiosInstance.interceptors.request.use((res: InternalAxiosRequestConfig) => {
  const controller = new AbortController()
  const url = res.url || ''
  res.signal = controller.signal
  abortControllerMap.set(url, controller)
  return res
})

let isRefreshing = false

axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    const url = res.config.url || ''
    abortControllerMap.delete(url)
    return res
  },
  async (error: AxiosError) => {
    console.log('err： ' + error)

    const userStore = useUserStoreWithOut()
    const originalRequest = error.config as InternalAxiosRequestConfig & { _isRetry?: boolean }

    if (error.response?.status === 401 && !originalRequest?._isRetry) {
      const refreshToken = userStore.getRefreshToken

      if (refreshToken && !isRefreshing) {
        isRefreshing = true
        try {
          const res = await axios.post(
            `${PATH_URL}/api/v1/auth/refresh`,
            {},
            {
              headers: { Authorization: `Bearer ${refreshToken}` }
            }
          )

          const data = res.data as any
          userStore.setToken(data.token || data.data?.token)
          userStore.setRefreshToken(data.refreshToken || data.data?.refreshToken)

          originalRequest._isRetry = true
          originalRequest.headers.Authorization = `Bearer ${userStore.getToken}`
          return axiosInstance(originalRequest)
        } catch {
          const currentRoute = window.location.pathname + window.location.search
          localStorage.setItem('redirectAfterLogin', currentRoute)
          userStore.logout()
          return Promise.reject(error)
        } finally {
          isRefreshing = false
        }
      } else if (!refreshToken) {
        const currentRoute = window.location.pathname + window.location.search
        localStorage.setItem('redirectAfterLogin', currentRoute)
        userStore.logout()
      }
      return Promise.reject(error)
    }

    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      ElMessage.error('请求超时，请稍后重试')
      return Promise.reject(error)
    }

    if (!navigator.onLine || error.message.includes('Network Error')) {
      ElMessage.error('网络连接失败，请检查网络')
      return Promise.reject(error)
    }

    if (error.response?.status) {
      const status = error.response.status
      if (status === 403) {
        const currentRoute = window.location.pathname + window.location.search
        localStorage.setItem('redirectAfterLogin', currentRoute)
        userStore.logout()
        return Promise.reject(error)
      }
      const errorMsg = HTTP_STATUS_MESSAGE[status] || `请求失败 (${status})`
      ElMessage.error(errorMsg)
      return Promise.reject(error)
    }

    ElMessage.error(error.message || '操作失败，请稍后重试')
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.request.use(defaultRequestInterceptors)
axiosInstance.interceptors.response.use(defaultResponseInterceptors)

const service = {
  request: (config: RequestConfig) => {
    return new Promise((resolve, reject) => {
      if (config.interceptors?.requestInterceptors) {
        config = config.interceptors.requestInterceptors(config as any)
      }

      axiosInstance
        .request(config)
        .then((res) => {
          resolve(res)
        })
        .catch((err: any) => {
          reject(err)
        })
    })
  },
  cancelRequest: (url: string | string[]) => {
    const urlList = Array.isArray(url) ? url : [url]
    for (const _url of urlList) {
      abortControllerMap.get(_url)?.abort()
      abortControllerMap.delete(_url)
    }
  },
  cancelAllRequest() {
    for (const [_, controller] of abortControllerMap) {
      controller.abort()
    }
    abortControllerMap.clear()
  }
}

export default service
